import { useMsal } from '@azure/msal-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Accordion,
  Box,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import AutoRenewingAdsTable from '../../components/dashboard/hostDashboard/AutoRenewingAdsTable'
import EarningsByLocationList from '../../components/dashboard/hostDashboard/EarningsByLocationList'
import EarningsSummaryCards from '../../components/dashboard/hostDashboard/EarningsSummaryCards'
import RecentPayoutsTable from '../../components/dashboard/hostDashboard/RecentPayoutsTable'
import { useSessionInfo } from '../../contexts/SessionContext'
import {
  AutoRenewingAdResponse,
  RecentTransferResponse,
  TransferByLocationResponse,
  TransferSummaryResponse,
  fetchAutoRenewingAds,
  fetchEarningsByLocation,
  fetchEarningsSummary,
  fetchRecentTransfers,
} from '../../data/hostDashboard.service'
import { COLORS } from '../../utils/constants'
import { formatNumber } from '../../utils/displayUtils'
import DashboardHeader from './DashboardHeader'

const calculateMonthlyRecurringIncome = (
  ads: AutoRenewingAdResponse[],
): number => {
  return ads.reduce((total, ad) => {
    const days = ad.numberOfDaysToRunAd ?? 0
    let monthlyAmount = 0

    const amount = ad.lastTransferAmount

    if (days === 7) {
      // Weekly: multiply by 4.33 (average weeks per month)
      monthlyAmount = amount * 4.33
    } else if (days === 14) {
      // Monthly: use as-is
      monthlyAmount = (amount * 4.33) / 2 // same as week calculation / 2
    } else if (days === 30) {
      // Monthly: use as-is
      monthlyAmount = amount
    } else if (days === 90) {
      // 90-day: divide by 3
      monthlyAmount = amount / 3
    }

    return total + monthlyAmount
  }, 0)
}

const HostDashboardPage = () => {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const [earningsSummary, setEarningsSummary] =
    useState<TransferSummaryResponse | null>(null)
  const [earningsByLocation, setEarningsByLocation] = useState<
    TransferByLocationResponse[]
  >([])
  const [autoRenewingAds, setAutoRenewingAds] = useState<
    AutoRenewingAdResponse[]
  >([])
  const [recentTransfers, setRecentTransfers] = useState<
    RecentTransferResponse[]
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    if (!session || !session.organizationId) {
      setError('No organization found in session.')
      setLoading(false)
      return
    }
    Promise.all([
      fetchEarningsSummary(instance, session.organizationId),
      fetchEarningsByLocation(instance, session.organizationId),
      fetchAutoRenewingAds(instance, session.organizationId),
      fetchRecentTransfers(instance, session.organizationId),
    ])
      .then(([summary, byLocation, autoAds, transactions]) => {
        setEarningsSummary(summary)
        setEarningsByLocation(byLocation)
        setAutoRenewingAds(autoAds)
        setRecentTransfers(transactions)
      })
      .catch((e) => {
        setError(e.message)
        console.log('API error:', e)
      })
      .finally(() => setLoading(false))
  }, [instance, session])

  if (loading) {
    return (
      <Stack>
        <DashboardHeader title='My Earnings' />
        <Stack gap='xl' p={24}>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing='md' mb='lg'>
            <Skeleton height={100} radius='md' />
            <Skeleton height={100} radius='md' />
            <Skeleton height={100} radius='md' />
          </SimpleGrid>
          <Skeleton height={120} radius='md' mb='lg' />
          <Skeleton height={140} radius='md' mb='lg' />
          <Skeleton height={140} radius='md' mb='lg' />
        </Stack>
      </Stack>
    )
  }

  const monthlyRecurringIncome =
    calculateMonthlyRecurringIncome(autoRenewingAds)

  return (
    <Stack>
      {/* Header */}
      <DashboardHeader title='My Earnings' />
      <Stack p={24}>
        {/* Error State */}
        {error && (
          <Paper withBorder p='md' radius='md' bg='red.0' mb='lg'>
            <Group>
              <FontAwesomeIcon
                icon={['fas', 'exclamation-circle']}
                color='red'
                size='lg'
              />
              <div>
                <Title order={4} c='red'>
                  Error
                </Title>
                <Text c='red.7'>{error}</Text>
              </div>
            </Group>
          </Paper>
        )}
        {/* Earnings Summary Cards */}
        {earningsSummary ? (
          <EarningsSummaryCards {...earningsSummary} />
        ) : (
          <Paper withBorder p='xl' radius='md' mb='lg'>
            <Stack align='center' gap='xs'>
              <FontAwesomeIcon
                icon={['fas', 'chart-line']}
                size='2x'
                color='#868e96'
              />
              <Title order={4} c='dimmed'>
                No Earnings Data
              </Title>
              <Text c='dimmed' ta='center'>
                Your earnings summary will appear here once you start receiving
                payments.
              </Text>
            </Stack>
          </Paper>
        )}
        {/* Earnings by Location */}
        {earningsByLocation && earningsByLocation.length > 1 && (
          <Box mb='lg'>
            <Title order={4} mb='xs'>
              Earnings by Location
            </Title>
            {earningsByLocation.length > 0 ? (
              <EarningsByLocationList locations={earningsByLocation} />
            ) : (
              <Paper withBorder p='xl' radius='md'>
                <Stack align='center' gap='xs'>
                  <FontAwesomeIcon
                    icon={['fas', 'map-marker-alt']}
                    size='2x'
                    color='#868e96'
                  />
                  <Title order={4} c='dimmed'>
                    No Location Data
                  </Title>
                  <Text c='dimmed' ta='center'>
                    Earnings by location will be displayed here after someone
                    advertises at one of your locations.
                  </Text>
                </Stack>
              </Paper>
            )}
          </Box>
        )}
        {/* Auto-Renewing Ads Accordion */}
        {autoRenewingAds && autoRenewingAds.length > 0 && (
          <Box mb='lg'>
            <Accordion
              variant='separated'
              radius='md'
              styles={{
                content: {
                  padding: 'var(--mantine-spacing-md)',
                },
                control: {
                  padding: 'var(--mantine-spacing-md)',
                  '&:hover': {
                    backgroundColor: 'var(--mantine-color-gray-0)',
                  },
                },
                item: {
                  border: '1px solid var(--mantine-color-gray-3)',
                  backgroundColor: 'var(--mantine-color-white)',
                },
              }}
            >
              <Accordion.Item value='recurring-income'>
                <Accordion.Control>
                  <Group justify='space-between' align='center' wrap='nowrap'>
                    <Group gap='xs'>
                      <FontAwesomeIcon
                        icon={['fas', 'sync']}
                        color='var(--mantine-color-blue-6)'
                      />
                      <div>
                        <Title order={4} mb={4}>
                          Recurring Ad Income
                        </Title>
                        <Text size='sm' c='dimmed'>
                          {autoRenewingAds.length} active recurring ads
                        </Text>
                      </div>
                    </Group>
                    <Group gap='xs' wrap='nowrap'>
                      <Text size='sm' c='dimmed'>
                        Estimated Monthly Recurring Earnings:
                      </Text>
                      <Text fw={600} size='lg' c={COLORS.LightGreen}>
                        ${formatNumber(monthlyRecurringIncome)}
                      </Text>
                    </Group>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <AutoRenewingAdsTable ads={autoRenewingAds} />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Box>
        )}
        {/* Recent Payouts Table */}
        <Box mb='lg'>
          <Title order={4} mb='xs'>
            Recent Payouts
          </Title>
          {recentTransfers && recentTransfers.length > 0 ? (
            <RecentPayoutsTable transfers={recentTransfers} />
          ) : (
            <Paper withBorder p='xl' radius='md'>
              <Stack align='center' gap='xs'>
                <FontAwesomeIcon
                  icon={['fas', 'receipt']}
                  size='2x'
                  color='#868e96'
                />
                <Title order={4} c='dimmed'>
                  No Recent Payouts
                </Title>
                <Text c='dimmed' ta='center'>
                  Your recent payouts will appear here once you start receiving
                  payments.
                </Text>
              </Stack>
            </Paper>
          )}
        </Box>
      </Stack>
    </Stack>
  )
}

export default HostDashboardPage
